import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Search, Menu, X, ChevronDown, Sparkles } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { CATEGORIES, formatINR } from "../data/products";
import { useCart } from "../context/CartContext";

const scrollToId = (href) => {
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -80 });
  else {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const selectTab = (catId) => {
  if (catId === "shantanu-nikhil") {
    scrollToId("#sn-collection");
    return;
  }
  window.dispatchEvent(new CustomEvent("select-tab", { detail: catId }));
  scrollToId("#shop");
};

const NAV_LINKS = [
  { label: "How It Works", action: () => scrollToId("#how-it-works") },
  { label: "Why Trust Us", action: () => scrollToId("#why-trust") },
];

const Navigation = () => {
  const { count, subtotal, openCart } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobile = (fn) => {
    setMobileOpen(false);
    setTimeout(fn, 80);
  };

  return (
    <>
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className={`fixed inset-x-0 top-[40px] z-[90] transition-all duration-500 ${
          scrolled ? "glass border-b border-[#F3E5AB]/12" : "bg-transparent"
        }`}
        data-testid="main-navigation"
      >
        <nav className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-6 md:px-10">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2"
            data-testid="nav-logo"
          >
            <Sparkles className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="font-serif text-xl tracking-tight text-[#F3EEE3] md:text-[1.35rem]">
              Diwali Gifts <span className="text-[#D4AF37]">In Two Days</span>
            </span>
          </button>

          <div className="hidden items-center gap-8 lg:flex">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1 font-sans text-sm tracking-wide text-[#E8E2D0] outline-none transition-colors duration-300 hover:text-[#D4AF37]"
                  data-testid="nav-gifting-hampers"
                >
                  Collections
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 glass text-[#F3EEE3]">
                {CATEGORIES.map((cat) => (
                  <DropdownMenuItem
                    key={cat.id}
                    onClick={() => selectTab(cat.id)}
                    data-testid={`nav-category-${cat.id}`}
                    className="flex cursor-pointer flex-col items-start gap-0.5 py-3 focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]"
                  >
                    <span className="font-serif text-lg text-[#F3EEE3]">{cat.name}</span>
                    <span className="font-sans text-xs text-[#8B93A7]">{cat.tagline}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {NAV_LINKS.map((link) => (
              <button
                key={link.label}
                onClick={link.action}
                className="font-sans text-sm tracking-wide text-[#E8E2D0] transition-colors duration-300 hover:text-[#D4AF37]"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.label}
              </button>
            ))}

            <a
              href="https://www.consortiumgifts.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-consortium-link"
              className="flex items-center gap-1 font-sans text-sm tracking-wide text-[#D4AF37] transition-colors duration-300 hover:text-[#F3E5AB]"
            >
              ConsortiumGifts.com <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
            </a>
          </div>

          <div className="flex items-center gap-1 md:gap-3">
            <button
              onClick={() => setSearchOpen((s) => !s)}
              aria-label="Search"
              data-testid="nav-search-toggle"
              className="flex h-10 w-10 items-center justify-center rounded-full text-[#E8E2D0] transition-colors duration-300 hover:text-[#D4AF37]"
            >
              <Search className="h-5 w-5" strokeWidth={1.5} />
            </button>

            <button
              onClick={openCart}
              aria-label="Open cart"
              data-testid="cart-drawer-toggle"
              className="relative flex items-center gap-2 rounded-full border border-[#F3E5AB]/15 px-3 py-2 text-[#E8E2D0] transition-all duration-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
            >
              <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
              <span className="hidden font-sans text-xs font-medium tabular-nums text-[#F3E5AB] sm:inline" data-testid="cart-total-nav">
                {formatINR(subtotal)}
              </span>
              <AnimatePresence>
                {count > 0 && (
                  <motion.span
                    key={count}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#D4AF37] font-sans text-[10px] font-semibold text-[#0B0F19]"
                    data-testid="cart-badge"
                  >
                    {count}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>

            <button
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="nav-hamburger"
              className="flex h-10 w-10 items-center justify-center rounded-full text-[#E8E2D0] transition-colors duration-300 hover:text-[#D4AF37] lg:hidden"
            >
              <Menu className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </nav>

        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden border-t border-[#F3E5AB]/10 glass"
            >
              <div className="mx-auto flex max-w-7xl items-center gap-3 px-6 py-4 md:px-10">
                <Search className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
                <input
                  autoFocus
                  placeholder="Search hampers, sweets, brass & corporate gifts…"
                  data-testid="nav-search-input"
                  className="w-full bg-transparent font-sans text-base text-[#F3EEE3] placeholder:text-[#8B93A7] outline-none"
                />
                <button onClick={() => setSearchOpen(false)} className="text-[#8B93A7] hover:text-[#F3EEE3]" aria-label="Close search">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm lg:hidden"
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="fixed right-0 top-0 z-[105] flex h-full w-80 max-w-[85vw] flex-col border-l border-[#F3E5AB]/12 bg-[#0d1220] p-8 lg:hidden"
              data-testid="mobile-menu"
            >
              <div className="mb-10 flex items-center justify-between">
                <span className="font-serif text-xl text-[#F3EEE3]">Menu</span>
                <button
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-[#F3E5AB]/12 text-[#8B93A7] hover:text-[#D4AF37]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <p className="mb-3 font-sans text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Collections</p>
              <div className="mb-6 space-y-1">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => closeMobile(() => selectTab(cat.id))}
                    data-testid={`mobile-category-${cat.id}`}
                    className="block w-full text-left font-serif text-2xl text-[#F3EEE3] transition-colors duration-300 hover:text-[#D4AF37]"
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="space-y-3 border-t border-[#F3E5AB]/10 pt-6">
                <button
                  onClick={() => closeMobile(() => scrollToId("#how-it-works"))}
                  className="block w-full text-left font-sans text-base text-[#E8E2D0] transition-colors duration-300 hover:text-[#D4AF37]"
                  data-testid="mobile-link-how"
                >
                  How It Works
                </button>
                <button
                  onClick={() => closeMobile(() => scrollToId("#why-trust"))}
                  className="block w-full text-left font-sans text-base text-[#E8E2D0] transition-colors duration-300 hover:text-[#D4AF37]"
                  data-testid="mobile-link-why"
                >
                  Why Trust Us
                </button>
                <a
                  href="https://www.consortiumgifts.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-consortium-link"
                  className="block w-full text-left font-sans text-base text-[#D4AF37] transition-colors duration-300 hover:text-[#F3E5AB]"
                >
                  ConsortiumGifts.com ↗
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
